import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * Captura cualquier excepción de React que de otra forma dejaría la pantalla en blanco.
 * Muestra un mensaje útil con el error y botones para recargar o volver al inicio.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // Log al backend si tienes un endpoint de error logging
        console.error("[ErrorBoundary]", error, info);
        this.setState({ info });
    }

    handleReload = () => {
        window.location.reload();
    };

    handleHome = () => {
        window.location.href = "/dashboard";
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        const errMsg = this.state.error?.message || String(this.state.error || "Error desconocido");
        const stack = this.state.info?.componentStack || this.state.error?.stack || "";

        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
                <div className="max-w-2xl w-full bg-white border border-slate-200 rounded-sm p-8 shadow-lg">
                    <div className="flex items-start gap-4 mb-4">
                        <div className="w-10 h-10 bg-red-100 rounded-sm flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                            <h1 className="font-display font-black text-2xl tracking-tight">Algo salió mal</h1>
                            <p className="text-sm text-slate-500 mt-1">
                                La aplicación encontró un error inesperado. Comparte esta información con soporte
                                si el problema persiste.
                            </p>
                        </div>
                    </div>

                    <div className="bg-slate-50 border-l-4 border-l-red-500 px-4 py-3 mb-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Error</div>
                        <div className="font-mono text-xs text-slate-900 break-words">{errMsg}</div>
                    </div>

                    {stack && (
                        <details className="mb-4">
                            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                                Detalle técnico (para soporte)
                            </summary>
                            <pre className="text-[10px] bg-slate-900 text-emerald-400 p-3 mt-2 rounded-sm overflow-x-auto max-h-60 font-mono leading-snug">
                                {stack.trim()}
                            </pre>
                        </details>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-slate-200">
                        <button
                            onClick={this.handleReload}
                            className="fm-btn-primary flex-1 justify-center"
                            data-testid="errboundary-reload"
                        >
                            <RefreshCw className="w-3.5 h-3.5" /> Recargar página
                        </button>
                        <button
                            onClick={this.handleHome}
                            className="fm-btn-outline flex-1 justify-center"
                            data-testid="errboundary-home"
                        >
                            <Home className="w-3.5 h-3.5" /> Volver al inicio
                        </button>
                    </div>

                    <div className="text-[10px] text-slate-400 text-center mt-4 font-mono">
                        FindMed Dispatch · v2.4 · Si persiste, envía screenshot a soporte
                    </div>
                </div>
            </div>
        );
    }
}
